import { useMemo, useState, type FormEvent } from "react";
import { Icon } from "../components/Icon";
import { Modal } from "../components/Modal";
import type { LatestPlanSnapshot, PlanProfile, WorkoutSession } from "../types";
import { completedPlanDates, exportPlanCalendar, formatDate, localDateKey } from "../utils";

interface PlanPageProps {
  snapshot: LatestPlanSnapshot | null;
  sessions: WorkoutSession[];
  rebuildPlan: (profile: PlanProfile, trigger: string) => Promise<LatestPlanSnapshot>;
  onError: (message: string) => void;
}

const defaultProfile = (): PlanProfile => ({
  goals: ["General Fitness"],
  targetDate: localDateKey(new Date(Date.now() + 90 * 86400000)),
  hoursPerDay: 1,
  constraints: [],
  age: "",
  sex: "",
  weight: "",
  height: "",
  fitnessLevel: "beginner",
  targetDistance: null,
  longestRun: "",
  weeklyVolume: "",
  trackNutrition: false,
  carbsPerDay: "",
  proteinPerDay: "",
  fatPerDay: "",
  foodDislikes: [],
});

export function PlanPage({ snapshot, sessions, rebuildPlan, onError }: PlanPageProps) {
  const [profileOpen, setProfileOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adapting, setAdapting] = useState(false);
  const [form, setForm] = useState<PlanProfile>(() => snapshot?.profile || defaultProfile());
  const completed = useMemo(() => completedPlanDates(sessions), [sessions]);

  function openProfile() {
    setForm(snapshot?.profile || defaultProfile());
    setProfileOpen(true);
  }

  async function saveProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    try {
      await rebuildPlan(form, snapshot ? "manual" : "onboarding");
      setProfileOpen(false);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Unable to build plan.");
    } finally {
      setSaving(false);
    }
  }

  async function adapt() {
    if (!snapshot) {
      openProfile();
      return;
    }
    setAdapting(true);
    try {
      await rebuildPlan(snapshot.profile, "recoveryDeviation");
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Unable to adapt plan.");
    } finally {
      setAdapting(false);
    }
  }

  return (
    <section className="route active-route">
      <header className="page-header actions-header">
        <div><p className="eyebrow">TRAINING BLOCK</p><h1>Your Plan</h1></div>
        <div className="header-actions">
          <button className="button secondary" type="button" onClick={openProfile}><Icon name="user" />Edit profile</button>
          <button className="button secondary" type="button" onClick={() => { if (!exportPlanCalendar(snapshot)) onError("There are no workouts to export."); }}><Icon name="download" />Calendar</button>
          <button className="button primary" type="button" disabled={adapting} onClick={() => void adapt()}><Icon name="refresh" />{adapting ? "Adapting…" : "Adapt plan"}</button>
        </div>
      </header>

      {snapshot?.plan.weekly_schedule?.length ? (
        <div className="plan-list">
          {snapshot.plan.weekly_schedule.map((week) => (
            <section className="week" key={week.week}>
              <header className="week-header"><strong>Week {week.week}</strong><span className="muted">{week.days.length} days</span></header>
              <div className="week-days">
                {week.days.map((day) => (
                  <article className={`day-row ${completed.has(day.date) ? "completed-row" : ""}`} key={`${week.week}-${day.date}-${day.type}`}>
                    <div className="day-date">{day.day || formatDate(day.date)}<br /><small>{formatDate(day.date, { month: "short", day: "numeric" })}</small></div>
                    <div className="day-main">
                      <strong>{day.title || day.type}</strong>
                      <small>{day.workout || day.notes || ""}</small>
                      {day.form_cues?.length ? <div className="inline-cues">{day.form_cues.map((cue) => <span key={cue}>{cue}</span>)}</div> : null}
                    </div>
                    <div className="day-end"><span className="status-label">{day.type}</span>{completed.has(day.date) ? <span className="complete-mark"><Icon name="check" /></span> : null}</div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <section className="card empty-state"><div><h2>No saved training plan</h2><p>Build a plan from your goals, schedule, and constraints.</p><button className="button primary" type="button" onClick={openProfile}>Build a plan</button></div></section>
      )}

      <Modal
        open={profileOpen}
        title="Goals and constraints"
        eyebrow="TRAINING PROFILE"
        wide
        onClose={() => setProfileOpen(false)}
        footer={null}
      >
        <form className="form-stack" onSubmit={saveProfile}>
          <div className="field-grid">
            <label className="field wide-field">Goals
              <input required value={form.goals.join(", ")} onChange={(event) => setForm({ ...form, goals: split(event.target.value) })} placeholder="Running, strength, general fitness" />
            </label>
            <label className="field">Target date<input required type="date" value={String(form.targetDate).slice(0, 10)} onChange={(event) => setForm({ ...form, targetDate: event.target.value })} /></label>
            <label className="field">Hours per day<input required type="number" min="0.25" max="8" step="0.25" value={form.hoursPerDay} onChange={(event) => setForm({ ...form, hoursPerDay: Number(event.target.value) })} /></label>
            <label className="field wide-field">Constraints<input value={form.constraints.join(", ")} onChange={(event) => setForm({ ...form, constraints: split(event.target.value) })} placeholder="Knee sensitivity, limited equipment" /></label>
            <label className="field">Age<input inputMode="numeric" value={form.age} onChange={(event) => setForm({ ...form, age: event.target.value })} /></label>
            <label className="field">Sex<select value={form.sex} onChange={(event) => setForm({ ...form, sex: event.target.value })}><option value="">Prefer not to say</option><option value="Female">Female</option><option value="Male">Male</option><option value="Other">Other</option></select></label>
            <label className="field">Weight<input value={form.weight} onChange={(event) => setForm({ ...form, weight: event.target.value })} placeholder="70 kg" /></label>
            <label className="field">Height<input value={form.height} onChange={(event) => setForm({ ...form, height: event.target.value })} placeholder="175 cm" /></label>
            <label className="field">Fitness level<select value={form.fitnessLevel} onChange={(event) => setForm({ ...form, fitnessLevel: event.target.value })}><option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option></select></label>
            <label className="field">Target distance<input value={form.targetDistance || ""} onChange={(event) => setForm({ ...form, targetDistance: event.target.value || null })} placeholder="10 km" /></label>
            <label className="switch-row wide-field"><input type="checkbox" checked={form.trackNutrition} onChange={(event) => setForm({ ...form, trackNutrition: event.target.checked })} /><span>Include nutrition plan</span></label>
          </div>
          <footer className="modal-footer"><button className="button text" type="button" onClick={() => setProfileOpen(false)}>Cancel</button><button className="button primary" type="submit" disabled={saving}>{saving ? "Building…" : "Save and rebuild plan"}</button></footer>
        </form>
      </Modal>
    </section>
  );
}

function split(value: string): string[] {
  return value.split(",").map((item) => item.trim()).filter(Boolean);
}
